import type { LayoutTree, Pane } from "@hq/contracts";
import {
  activateTab,
  activePane,
  allPanes,
  createTree,
  deserialize,
  findPane,
  focusPane,
  openPane as layoutOpenPane,
  replacePane,
  splitPane,
  updatePaneParams,
} from "@hq/layout";
import { paneInit, type PaneSpec } from "./paneTypes";

export const LAYOUT_KV_KEY = "workspace.layout";

/** Daily notes (pinned: it is the home) beside Today. */
export function defaultTree(): LayoutTree {
  const tree = createTree(paneInit({ paneType: "daily" }));
  const daily = allPanes(tree)[0]!;
  return pinPane(
    splitPane(tree, daily.id, "horizontal", paneInit({ paneType: "today" }), "after"),
    daily.id,
  );
}

export function restoreTree(json: string | undefined): LayoutTree {
  if (!json) return defaultTree();
  const tree = deserialize(json);
  return tree && allPanes(tree).length > 0 ? tree : defaultTree();
}

export function focusedPane(tree: LayoutTree): Pane | undefined {
  return tree.focusedPaneId ? findPane(tree, tree.focusedPaneId)?.pane : undefined;
}

/** Transient, app-level pane flags kept in `params`. They never take part in dedupe. */
const TRANSIENT_PARAMS = new Set(["autoFocus", "pinned", "preview", "selectedNoteId"]);

export function isPinned(pane: Pane): boolean {
  return pane.params.pinned === true;
}

export function isPreview(pane: Pane): boolean {
  return pane.params.preview === true;
}

function identity(params: Record<string, unknown>): string {
  const entries = Object.entries(params)
    .filter(([k]) => !TRANSIENT_PARAMS.has(k))
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(entries);
}

export type OpenMode =
  /** Replace the focused group's active pane unless it is pinned (navigation, list opens). */
  | "replace"
  /** Always add a tab to the focused group. */
  | "tab";

export type OpenOptions = {
  mode?: OpenMode;
  /** The new pane starts pinned (you are about to edit it). */
  pinned?: boolean;
  /** The new pane is a preview: the next replace-open takes its place. */
  preview?: boolean;
};

function withFlags(params: Record<string, unknown>, opts: OpenOptions, spec: PaneSpec) {
  const next: Record<string, unknown> = { ...params };
  delete next.preview;
  delete next.pinned;
  if (opts.pinned) next.pinned = true;
  else if (opts.preview) next.preview = true;
  if (spec.paneType === "note" && spec.autoFocus) next.autoFocus = true;
  return next;
}

/**
 * Open a pane with VS Code-like tab semantics. A pane that is already open anywhere is
 * activated (and pinned if asked). Otherwise `replace` swaps the focused group's active pane
 * unless that pane is pinned, in which case a new tab is added; `tab` always adds one.
 */
export function openSpec(tree: LayoutTree, spec: PaneSpec, opts: OpenOptions = {}): LayoutTree {
  const init = paneInit(spec);
  const wanted = identity(init.params ?? {});
  const existing = allPanes(tree).find(
    (p) => p.paneType === init.paneType && identity(p.params) === wanted,
  );
  if (existing) {
    let next = focusPane(activateTab(tree, existing.id), existing.id);
    const params: Record<string, unknown> = { ...existing.params };
    if (opts.pinned) {
      delete params.preview;
      params.pinned = true;
    }
    if (spec.paneType === "note" && spec.autoFocus) params.autoFocus = true;
    if (
      identity(params) !== identity(existing.params) ||
      JSON.stringify(params) !== JSON.stringify(existing.params)
    ) {
      next = updatePaneParams(next, existing.id, params);
    }
    return next;
  }

  const flagged = { ...init, params: withFlags(init.params ?? {}, opts, spec) };
  const focused = focusedPane(tree);
  const mode = opts.mode ?? "replace";
  if (mode === "replace" && focused && !isPinned(focused)) {
    return replacePane(tree, focused.id, flagged);
  }
  const groupId = focused ? findPane(tree, focused.id)?.group.id : undefined;
  return layoutOpenPane(tree, flagged, groupId ? { groupId, dedupe: false } : { dedupe: false });
}

export function pinPane(tree: LayoutTree, paneId: string): LayoutTree {
  const found = findPane(tree, paneId);
  if (!found || isPinned(found.pane)) return tree;
  const { preview: _p, ...rest } = found.pane.params;
  return updatePaneParams(tree, paneId, { ...rest, pinned: true });
}

export function unpinPane(tree: LayoutTree, paneId: string): LayoutTree {
  const found = findPane(tree, paneId);
  if (!found || !isPinned(found.pane)) return tree;
  const { pinned: _p, ...rest } = found.pane.params;
  return updatePaneParams(tree, paneId, rest);
}

/** Clear a transient flag once the pane consumed it, so a persisted layout does not replay it. */
export function clearAutoFocus(tree: LayoutTree, paneId: string): LayoutTree {
  const found = findPane(tree, paneId);
  if (!found || !found.pane.params.autoFocus) return tree;
  const { autoFocus: _drop, ...rest } = found.pane.params;
  return updatePaneParams(tree, paneId, rest);
}

export { activePane };
